/**
 * Macmerise Custom Swatches System
 * Replacement for Swatch King app
 *
 * @version 1.0.0
 * @author Macmerise
 *
 * Features:
 * - Per-option styling (circular, pill, polaroid, dropdown)
 * - Custom color swatches with hex codes and images
 * - Hover-to-preview image switching
 * - Polaroid style with image + name + price
 * - Works on product pages and collection pages
 */

(function() {
  'use strict';

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================

  class MacmeriseSwatches {
    constructor() {
      this.config = null;
      this.pageType = this.detectPageType();
      this.initialized = false;

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    // --------------------------------------------------------------------------
    // Initialization
    // --------------------------------------------------------------------------

    init() {
      this.loadConfig();

      if (!this.config) {
        console.warn('Macmerise Swatches: Configuration not found');
        return;
      }

      this.injectStyles();
      this.processVariantPickers();
      // NOTE: Variant image filtering is now handled by macmerise-variant-images.js
      // this.setupVariantImageFiltering();
      this.setupMutationObserver();
      this.initialized = true;

      console.log('Macmerise Swatches: Initialized successfully');
    }

    loadConfig() {
      const configElement = document.getElementById('macmerise-swatches-config');
      if (configElement) {
        try {
          this.config = JSON.parse(configElement.textContent);
        } catch (e) {
          console.error('Macmerise Swatches: Failed to parse configuration', e);
        }
      }
    }

    detectPageType() {
      const path = window.location.pathname;
      if (path.includes('/products/')) return 'product_page';
      if (path.includes('/collections/')) return 'collection_page';
      if (path === '/' || path === '') return 'home_page';
      return 'other';
    }

    // --------------------------------------------------------------------------
    // Style Injection
    // --------------------------------------------------------------------------

    injectStyles() {
      if (document.getElementById('macmerise-swatches-styles')) return;

      const styles = document.createElement('style');
      styles.id = 'macmerise-swatches-styles';
      styles.textContent = this.getStyles();
      document.head.appendChild(styles);
    }

    getStyles() {
      return `
        /* Macmerise Swatches - Custom Styles */

        /* ===== Circular Swatches ===== */
        .mm-swatch-circular {
          display: inline-block;
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          position: relative;
          background-size: cover;
          background-position: center;
          flex-shrink: 0;
          aspect-ratio: 1 / 1;
        }

        .mm-swatch-circular.mm-size-small {
          width: 24px;
          height: 24px;
          min-width: 24px;
          min-height: 24px;
        }

        .mm-swatch-circular.mm-size-medium {
          width: 36px;
          height: 36px;
          min-width: 36px;
          min-height: 36px;
        }

        .mm-swatch-circular.mm-size-large {
          width: 48px;
          height: 48px;
          min-width: 48px;
          min-height: 48px;
        }

        .mm-swatch-circular:hover {
          transform: scale(1.1);
        }

        .mm-swatch-circular.mm-selected {
          outline: 2px solid #141413;
          outline-offset: 2px;
        }

        .mm-swatch-circular.mm-unavailable {
          opacity: 0.5 !important;
          position: relative !important;
          cursor: pointer !important;
        }

        .mm-swatch-circular.mm-unavailable::before {
          content: '' !important;
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background: linear-gradient(
            to top right,
            transparent 0%,
            transparent calc(50% - 1px),
            rgba(0, 0, 0, 0.8) calc(50% - 1px),
            rgba(0, 0, 0, 0.8) calc(50% + 1px),
            transparent calc(50% + 1px),
            transparent 100%
          ) !important;
          border-radius: 50% !important;
          pointer-events: none !important;
          z-index: 10 !important;
        }

        /* White strikethrough for dark colors */
        .mm-swatch-circular.mm-unavailable.mm-dark-swatch::before {
          background: linear-gradient(
            to top right,
            transparent 0%,
            transparent calc(50% - 1px),
            rgba(255, 255, 255, 0.95) calc(50% - 1px),
            rgba(255, 255, 255, 0.95) calc(50% + 1px),
            transparent calc(50% + 1px),
            transparent 100%
          ) !important;
        }

        .mm-swatch-circular.mm-white {
          box-shadow: inset 0 0 0 1px #ddd;
        }

        /* ===== Pill/Button Swatches ===== */
        .mm-swatch-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 40px;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 20px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          background: #fff;
          transition: all 0.15s ease;
          white-space: nowrap;
          box-sizing: border-box;
        }

        .mm-swatch-pill:hover {
          border-color: #141413;
        }

        .mm-swatch-pill.mm-selected {
          background: #141413;
          color: #fff;
          border-color: #141413;
        }

        .mm-swatch-pill.mm-unavailable {
          opacity: 0.5 !important;
          position: relative !important;
          cursor: pointer !important;
        }

        .mm-swatch-pill.mm-unavailable::before {
          content: '' !important;
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background: linear-gradient(
            to top right,
            transparent 0%,
            transparent calc(50% - 1px),
            rgba(0, 0, 0, 0.7) calc(50% - 1px),
            rgba(0, 0, 0, 0.7) calc(50% + 1px),
            transparent calc(50% + 1px),
            transparent 100%
          ) !important;
          border-radius: 20px !important;
          pointer-events: none !important;
          z-index: 10 !important;
        }

        .mm-swatch-pill.mm-unavailable.mm-dark-swatch::before {
          background: linear-gradient(
            to top right,
            transparent 0%,
            transparent calc(50% - 1px),
            rgba(255, 255, 255, 0.95) calc(50% - 1px),
            rgba(255, 255, 255, 0.95) calc(50% + 1px),
            transparent calc(50% + 1px),
            transparent 100%
          ) !important;
        }

        /* ===== Polaroid Swatches ===== */
        .mm-swatch-polaroid-container {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .mm-swatch-polaroid {
          display: flex;
          flex-direction: column;
          width: 80px;
          cursor: pointer;
          transition: all 0.15s ease;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid transparent;
          background: #fff;
        }

        .mm-swatch-polaroid:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .mm-swatch-polaroid.mm-selected {
          border-color: #141413;
        }

        .mm-swatch-polaroid.mm-unavailable {
          opacity: 0.5 !important;
          position: relative !important;
          cursor: pointer !important;
        }

        .mm-swatch-polaroid.mm-unavailable::before {
          content: '' !important;
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background: linear-gradient(
            to top right,
            transparent 0%,
            transparent calc(50% - 1px),
            rgba(0, 0, 0, 0.7) calc(50% - 1px),
            rgba(0, 0, 0, 0.7) calc(50% + 1px),
            transparent calc(50% + 1px),
            transparent 100%
          ) !important;
          border-radius: 8px !important;
          pointer-events: none !important;
          z-index: 10 !important;
        }

        .mm-swatch-polaroid.mm-unavailable.mm-dark-swatch::before {
          background: linear-gradient(
            to top right,
            transparent 0%,
            transparent calc(50% - 1px),
            rgba(255, 255, 255, 0.95) calc(50% - 1px),
            rgba(255, 255, 255, 0.95) calc(50% + 1px),
            transparent calc(50% + 1px),
            transparent 100%
          ) !important;
        }

        .mm-swatch-polaroid__image {
          width: 100%;
          aspect-ratio: 1;
          object-fit: cover;
          background: #f5f5f5;
        }

        .mm-swatch-polaroid__info {
          padding: 6px 8px;
          text-align: left;
        }

        .mm-swatch-polaroid__name {
          font-size: 11px;
          font-weight: 500;
          line-height: 1.2;
          color: #141413;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mm-swatch-polaroid__price {
          font-size: 11px;
          color: #666;
          margin-top: 2px;
        }

        /* ===== Dropdown with Label ===== */
        .mm-swatch-dropdown-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          background: #fff;
          min-width: 200px;
        }

        .mm-swatch-dropdown-label:hover {
          border-color: #141413;
        }

        .mm-swatch-dropdown-label__text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .mm-swatch-dropdown-label__label {
          font-size: 12px;
          color: #666;
        }

        .mm-swatch-dropdown-label__value {
          font-size: 14px;
          font-weight: 500;
          color: #141413;
        }

        .mm-swatch-dropdown-label__price {
          font-size: 14px;
          color: #141413;
        }

        .mm-swatch-dropdown-label__chevron {
          width: 20px;
          height: 20px;
          stroke: #666;
        }

        /* ===== Swatch Container ===== */
        .mm-swatches-wrapper {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }

        .mm-swatches-wrapper.mm-style-polaroid {
          gap: 12px;
        }

        .mm-option-label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .mm-option-label__name {
          color: #666;
        }

        .mm-option-label__value {
          font-weight: 500;
          color: #141413;
        }

        /* ===== Badges ===== */
        .mm-badge {
          display: inline-block;
          padding: 2px 6px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          border-radius: 2px;
          position: absolute;
          top: 4px;
          left: 4px;
        }

        .mm-badge--new {
          background: #121212;
          color: #fff;
        }

        .mm-badge--sale {
          background: #E53935;
          color: #fff;
        }

        .mm-badge--soldout {
          background: #9E9E9E;
          color: #fff;
        }

        /* ===== Animations ===== */
        @keyframes mm-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mm-animate-in {
          animation: mm-fade-in 0.2s ease forwards;
        }

        /* ===== Hidden state ===== */
        .mm-hidden {
          display: none !important;
        }

        /* ===== Gallery Image Filtering ===== */
        .product-gallery__media.mm-gallery-hidden,
        .product-gallery__thumbnail.mm-gallery-hidden {
          display: none !important;
          visibility: hidden !important;
          width: 0 !important;
          height: 0 !important;
          overflow: hidden !important;
          position: absolute !important;
          pointer-events: none !important;
        }

        /* ===== Product Card Swatches (Collection Page) ===== */
        .mm-card-swatches {
          display: flex;
          gap: 4px;
          margin-top: 8px;
        }

        .mm-card-swatches .mm-swatch-circular {
          width: 20px;
          height: 20px;
        }

        .mm-card-swatches .mm-more-count {
          font-size: 11px;
          color: #666;
          display: flex;
          align-items: center;
        }
      `;
    }

    // --------------------------------------------------------------------------
    // Variant Picker Processing
    // --------------------------------------------------------------------------

    processVariantPickers() {
      // Process product page variant pickers
      const variantPickers = document.querySelectorAll('variant-picker');
      variantPickers.forEach(picker => this.processVariantPicker(picker));

      // Process collection page product cards
      const productCards = document.querySelectorAll('product-card');
      productCards.forEach(card => this.processProductCard(card));
    }

    processVariantPicker(picker) {
      if (picker.dataset.mmProcessed) return;
      picker.dataset.mmProcessed = 'true';

      const optionFieldsets = picker.querySelectorAll('.variant-picker__option');

      optionFieldsets.forEach(fieldset => {
        const legend = fieldset.querySelector('legend');
        const optionName = legend ? legend.textContent.replace(':', '').trim().toLowerCase() : '';

        const styleConfig = this.getOptionStyle(optionName, 'product_page');
        if (!styleConfig || styleConfig.style === 'default') return;

        this.transformOptionFieldset(fieldset, optionName, styleConfig);
      });

      // Setup hover preview functionality
      this.setupHoverPreview(picker);
    }

    processProductCard(card) {
      if (card.dataset.mmProcessed) return;
      card.dataset.mmProcessed = 'true';

      const swatchList = card.querySelector('.product-card__swatch-list, .product-card__variant-list');
      if (!swatchList) return;

      const optionPosition = swatchList.dataset.optionPosition;
      // Product cards already have swatches, we just need to ensure styling matches
    }

    getOptionStyle(optionName, pageType) {
      const pageConfig = this.config.option_styles[pageType];
      if (!pageConfig) return null;

      return pageConfig[optionName.toLowerCase()] || null;
    }

    // --------------------------------------------------------------------------
    // Option Transformation
    // --------------------------------------------------------------------------

    transformOptionFieldset(fieldset, optionName, styleConfig) {
      const { style } = styleConfig;

      switch (style) {
        case 'circular':
          this.transformToCircular(fieldset, optionName, styleConfig);
          break;
        case 'pill':
          this.transformToPill(fieldset, optionName, styleConfig);
          break;
        case 'polaroid':
          this.transformToPolaroid(fieldset, optionName, styleConfig);
          break;
        case 'dropdown_label':
          this.transformToDropdownLabel(fieldset, optionName, styleConfig);
          break;
        case 'dropdown':
          // Keep default dropdown behavior
          break;
        case 'hidden':
          fieldset.classList.add('mm-hidden');
          break;
      }
    }

    transformToCircular(fieldset, optionName, styleConfig) {
      const valuesContainer = fieldset.querySelector('.variant-picker__option-values');
      if (!valuesContainer) return;

      const inputs = valuesContainer.querySelectorAll('input[type="radio"]');

      inputs.forEach(input => {
        const label = input.nextElementSibling;
        if (!label) return;

        const optionValue = this.getOptionValueFromInput(input);

        // Store the option value for later retrieval (since we hide the label)
        input.dataset.optionValue = optionValue;

        const swatchColor = this.getSwatchColor(optionValue);

        // Create a new circular swatch element
        const swatch = document.createElement('span');
        swatch.className = `mm-swatch-circular mm-size-${styleConfig.size || 'medium'}`;
        swatch.setAttribute('data-tooltip', optionValue);
        swatch.title = optionValue;

        // Apply color/image
        if (swatchColor) {
          if (swatchColor.startsWith('http') || swatchColor.startsWith('//')) {
            swatch.style.backgroundImage = `url(${swatchColor})`;
          } else {
            swatch.style.backgroundColor = swatchColor;
            if (this.isLightColor(swatchColor)) {
              swatch.classList.add('mm-white');
            }
          }
        } else {
          // Fallback: use the color name as background
          swatch.style.backgroundColor = optionValue.toLowerCase();
        }

        // Handle selected state
        if (input.checked) {
          swatch.classList.add('mm-selected');
        }

        // Handle availability - check multiple indicators
        const isUnavailable = input.disabled ||
          input.closest('label')?.classList.contains('is-disabled') ||
          input.parentElement?.classList.contains('is-disabled') ||
          !this.isColorAvailable(optionValue);

        // If hide_sold_out_variants is enabled, skip unavailable variants entirely
        if (isUnavailable && this.config.settings.hide_sold_out_variants) {
          label.style.display = 'none';
          return; // Skip this variant
        }

        if (isUnavailable) {
          swatch.classList.add('mm-unavailable');

          // Add dark-swatch class for dark colors (white strikethrough)
          if (this.isDarkColor(optionValue)) {
            swatch.classList.add('mm-dark-swatch');
          }
        }

        // Click handler - works even for unavailable/disabled variants
        swatch.addEventListener('click', () => {
          console.log('[Swatches] Swatch clicked:', optionValue);

          // Temporarily enable if disabled, so we can select it
          const wasDisabled = input.disabled;
          if (wasDisabled) {
            input.disabled = false;
          }

          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));

          // Dispatch custom event for variant images (works even for out-of-stock)
          // Dispatch on both document and window to ensure it's received
          const colorEvent = new CustomEvent('macmerise:colorchange', {
            detail: { color: optionValue },
            bubbles: true
          });
          document.dispatchEvent(colorEvent);
          window.dispatchEvent(colorEvent);

          console.log('[Swatches] Dispatched colorchange event for:', optionValue);

          // Re-disable if it was disabled
          if (wasDisabled) {
            input.disabled = true;
          }

          // Update selected state for all swatches
          valuesContainer.querySelectorAll('.mm-swatch-circular').forEach(s => s.classList.remove('mm-selected'));
          swatch.classList.add('mm-selected');
        });

        // Update on change
        input.addEventListener('change', () => {
          valuesContainer.querySelectorAll('.mm-swatch-circular').forEach(s => s.classList.remove('mm-selected'));
          if (input.checked) {
            swatch.classList.add('mm-selected');
          }
        });

        // Hover preview
        if (this.config.settings.switch_image_on_hover) {
          swatch.addEventListener('pointerenter', () => {
            this.previewVariantImage(input);
          });
          swatch.addEventListener('pointerleave', () => {
            this.revertVariantImage();
          });
        }

        // Hide original label and insert swatch
        label.style.display = 'none';
        label.parentNode.insertBefore(swatch, label);
      });

      valuesContainer.classList.add('mm-swatches-wrapper');
    }

    transformToPill(fieldset, optionName, styleConfig) {
      const valuesContainer = fieldset.querySelector('.variant-picker__option-values');
      if (!valuesContainer) return;

      const inputs = valuesContainer.querySelectorAll('input[type="radio"]');

      inputs.forEach(input => {
        const label = input.nextElementSibling;
        if (!label) return;

        // Add pill classes
        label.classList.add('mm-swatch-pill');

        // Handle selected state
        if (input.checked) {
          label.classList.add('mm-selected');
        }

        // Handle availability
        const isUnavailable = input.disabled ||
          input.closest('label')?.classList.contains('is-disabled') ||
          input.parentElement?.classList.contains('is-disabled');

        // If hide_sold_out_variants is enabled, skip unavailable variants entirely
        if (isUnavailable && this.config.settings.hide_sold_out_variants) {
          label.style.display = 'none';
          return; // Skip this variant
        }

        if (isUnavailable) {
          label.classList.add('mm-unavailable');
        }

        // Update on change
        input.addEventListener('change', () => {
          valuesContainer.querySelectorAll('.mm-swatch-pill').forEach(l => l.classList.remove('mm-selected'));
          if (input.checked) {
            label.classList.add('mm-selected');
          }
        });
      });

      valuesContainer.classList.add('mm-swatches-wrapper');
    }

    transformToPolaroid(fieldset, optionName, styleConfig) {
      const valuesContainer = fieldset.querySelector('.variant-picker__option-values');
      if (!valuesContainer) return;

      const inputs = valuesContainer.querySelectorAll('input[type="radio"]');

      inputs.forEach(input => {
        const label = input.nextElementSibling;
        if (!label) return;

        const optionValue = this.getOptionValueFromInput(input);
        const variantData = this.getVariantDataForOption(input);

        // Handle availability
        const isUnavailable = input.disabled ||
          input.closest('label')?.classList.contains('is-disabled') ||
          input.parentElement?.classList.contains('is-disabled');

        // If hide_sold_out_variants is enabled, skip unavailable variants entirely
        if (isUnavailable && this.config.settings.hide_sold_out_variants) {
          label.style.display = 'none';
          return; // Skip this variant
        }

        // Create polaroid structure
        const polaroid = document.createElement('div');
        polaroid.className = 'mm-swatch-polaroid';
        if (input.checked) polaroid.classList.add('mm-selected');
        if (isUnavailable) polaroid.classList.add('mm-unavailable');

        // Image
        const img = document.createElement('img');
        img.className = 'mm-swatch-polaroid__image';
        img.loading = 'lazy';
        img.alt = optionValue;

        let imageSrc = null;

        // Try multiple sources for image
        // 1. From variant data
        if (variantData && variantData.featured_media) {
          imageSrc = variantData.featured_media.src || variantData.featured_media.preview_image?.src;
        }

        // 2. From input's variant media attribute
        if (!imageSrc && input.dataset.variantMedia) {
          try {
            const mediaData = JSON.parse(input.dataset.variantMedia);
            imageSrc = mediaData.src || mediaData.preview_image?.src;
          } catch (e) {}
        }

        // 3. From existing image in label (theme might have rendered it)
        if (!imageSrc && label) {
          const existingImg = label.querySelector('img');
          if (existingImg && existingImg.src) {
            imageSrc = existingImg.src;
          }
        }

        // 4. From thumbnail swatch if it exists
        if (!imageSrc && label && label.classList.contains('thumbnail-swatch')) {
          const thumbImg = label.querySelector('img');
          if (thumbImg) {
            imageSrc = thumbImg.src;
          }
        }

        // Set the image source
        if (imageSrc) {
          img.src = this.buildImageUrl(imageSrc, 160);
        } else {
          // Placeholder
          img.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="#f0f0f0" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="#999" font-size="12">No Image</text></svg>');
        }

        polaroid.appendChild(img);

        // Info section
        if (styleConfig.show_name || styleConfig.show_price) {
          const info = document.createElement('div');
          info.className = 'mm-swatch-polaroid__info';

          if (styleConfig.show_name) {
            const name = document.createElement('div');
            name.className = 'mm-swatch-polaroid__name';
            name.textContent = this.truncateText(optionValue, 15);
            name.title = optionValue;
            info.appendChild(name);
          }

          if (styleConfig.show_price && variantData && variantData.price) {
            const price = document.createElement('div');
            price.className = 'mm-swatch-polaroid__price';
            price.textContent = this.formatPrice(variantData.price);
            info.appendChild(price);
          }

          polaroid.appendChild(info);
        }

        // Click handler
        polaroid.addEventListener('click', () => {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));

          // Update selected state
          valuesContainer.querySelectorAll('.mm-swatch-polaroid').forEach(p => p.classList.remove('mm-selected'));
          polaroid.classList.add('mm-selected');
        });

        // Hover preview
        if (this.config.settings.switch_image_on_hover) {
          polaroid.addEventListener('pointerenter', () => {
            this.previewVariantImage(input);
          });

          polaroid.addEventListener('pointerleave', () => {
            this.revertVariantImage();
          });
        }

        // Replace original label
        label.style.display = 'none';
        label.parentNode.insertBefore(polaroid, label.nextSibling);
      });

      valuesContainer.classList.add('mm-swatches-wrapper', 'mm-style-polaroid');
    }

    transformToDropdownLabel(fieldset, optionName, styleConfig) {
      // This uses the theme's native dropdown but with enhanced styling
      const selectButton = fieldset.querySelector('.select');
      if (selectButton) {
        selectButton.classList.add('mm-swatch-dropdown-label');
      }
    }

    // --------------------------------------------------------------------------
    // Hover Preview
    // --------------------------------------------------------------------------

    setupHoverPreview(picker) {
      if (!this.config.settings.switch_image_on_hover) return;

      const form = picker.closest('form') || document.querySelector('form[data-product-form]');
      if (!form) return;

      this.previewState = {
        originalImage: null,
        gallery: document.querySelector('product-gallery')
      };
    }

    previewVariantImage(input) {
      if (!this.previewState || !this.previewState.gallery) return;

      const primaryImage = this.previewState.gallery.querySelector('.product-gallery__media img');
      if (!primaryImage) return;

      // Store original
      if (!this.previewState.originalImage) {
        this.previewState.originalImage = {
          src: primaryImage.src,
          srcset: primaryImage.srcset
        };
      }

      // Get variant image
      let newSrc = null;
      if (input.dataset.variantMedia) {
        try {
          const mediaData = JSON.parse(input.dataset.variantMedia);
          newSrc = this.buildImageUrl(mediaData.src || mediaData.preview_image?.src, 800);
        } catch (e) {}
      }

      if (newSrc && primaryImage.src !== newSrc) {
        primaryImage.style.transition = `opacity ${this.config.settings.transition_duration}ms ease`;
        primaryImage.style.opacity = '0.7';

        const img = new Image();
        img.onload = () => {
          primaryImage.src = newSrc;
          primaryImage.style.opacity = '1';
        };
        img.src = newSrc;
      }
    }

    revertVariantImage() {
      if (!this.previewState || !this.previewState.originalImage) return;

      const primaryImage = this.previewState.gallery?.querySelector('.product-gallery__media img');
      if (!primaryImage) return;

      if (primaryImage.src !== this.previewState.originalImage.src) {
        primaryImage.style.opacity = '0.7';

        const img = new Image();
        img.onload = () => {
          primaryImage.src = this.previewState.originalImage.src;
          if (this.previewState.originalImage.srcset) {
            primaryImage.srcset = this.previewState.originalImage.srcset;
          }
          primaryImage.style.opacity = '1';
        };
        img.src = this.previewState.originalImage.src;
      }
    }

    // --------------------------------------------------------------------------
    // Mutation Observer (for dynamic content)
    // --------------------------------------------------------------------------

    setupMutationObserver() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for new variant pickers
              if (node.matches && node.matches('variant-picker')) {
                this.processVariantPicker(node);
              }
              // Check for variant pickers inside added node
              const pickers = node.querySelectorAll ? node.querySelectorAll('variant-picker:not([data-mm-processed])') : [];
              pickers.forEach(picker => this.processVariantPicker(picker));

              // Check for product cards
              const cards = node.querySelectorAll ? node.querySelectorAll('product-card:not([data-mm-processed])') : [];
              cards.forEach(card => this.processProductCard(card));
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    // --------------------------------------------------------------------------
    // Utility Functions
    // --------------------------------------------------------------------------

    getOptionValueFromInput(input) {
      // 1. Check if we stored the value as a data attribute
      if (input.dataset.optionValue) {
        return input.dataset.optionValue;
      }

      // 2. Try to get the value from the associated label
      const label = input.nextElementSibling;
      if (label) {
        // Check for our custom swatch with tooltip
        const swatch = label.querySelector('[data-tooltip]');
        if (swatch && swatch.dataset.tooltip) {
          return swatch.dataset.tooltip;
        }

        // For swatches, the label might have sr-only text
        const srOnly = label.querySelector('.sr-only');
        if (srOnly && srOnly.textContent.trim()) {
          return srOnly.textContent.trim();
        }

        // Get visible text (filter out hidden elements)
        const text = label.textContent.trim();
        if (text) return text;
      }

      // 3. Fallback to input value
      return input.value;
    }

    getSwatchColor(colorName) {
      const normalized = colorName.toLowerCase().trim();
      return this.config.color_swatches[normalized] || null;
    }

    getVariantDataForOption(input) {
      // Try multiple sources for variant data
      const optionValue = this.getOptionValueFromInput(input);
      const optionPosition = input.dataset.optionPosition;

      // 1. Check if input has variant media data attribute (theme provides this)
      if (input.dataset.variantMedia) {
        try {
          const media = JSON.parse(input.dataset.variantMedia);
          return { featured_media: media };
        } catch (e) {}
      }

      // 2. Check the label for an existing image (theme might have rendered it)
      const label = input.nextElementSibling;
      if (label) {
        const existingImg = label.querySelector('img');
        if (existingImg && existingImg.src) {
          return { featured_media: { src: existingImg.src } };
        }
      }

      // 3. Check our custom product data (from macmerise-swatches-config.liquid)
      const productDataEl = document.getElementById('macmerise-product-data');
      if (productDataEl) {
        try {
          const productData = JSON.parse(productDataEl.textContent);
          if (productData && productData.variants) {
            const optionKey = `option${optionPosition}`;

            // First: try to find variant with this option AND featured_media
            let matchingVariant = productData.variants.find(v =>
              v[optionKey] === optionValue && v.featured_media
            );

            // Second: if not found, try ANY option position (in case position is wrong)
            if (!matchingVariant) {
              matchingVariant = productData.variants.find(v =>
                (v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue) && v.featured_media
              );
            }

            // Third: if still not found, find variant with this option (even without image)
            // Then try to find image from product gallery
            if (!matchingVariant) {
              matchingVariant = productData.variants.find(v =>
                v[optionKey] === optionValue || v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue
              );

              // Try to get image from product gallery based on variant
              if (matchingVariant && !matchingVariant.featured_media) {
                const galleryImage = this.findImageFromGallery(optionValue);
                if (galleryImage) {
                  matchingVariant = { ...matchingVariant, featured_media: { src: galleryImage } };
                }
              }
            }

            if (matchingVariant) {
              return matchingVariant;
            }
          }
        } catch (e) {
          console.error('Error parsing product data:', e);
        }
      }

      // 4. Look for product JSON in the page (Impact theme data)
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          const product = JSON.parse(productJson.textContent);
          // Try by variant ID if available
          const variantId = input.dataset.variantId || input.value;
          let variant = product.variants?.find(v => v.id == variantId);

          // Try by option value
          if (!variant && product.variants) {
            variant = product.variants.find(v =>
              (v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue) && v.featured_media
            );
          }

          if (variant) return variant;
        } catch (e) {}
      }

      // 5. Look for variant picker's internal data
      const variantPicker = input.closest('variant-picker');
      if (variantPicker) {
        const variantScript = variantPicker.querySelector('script[data-variant]');
        if (variantScript) {
          try {
            const variant = JSON.parse(variantScript.textContent);
            if (variant && variant.featured_media) {
              return variant;
            }
          } catch (e) {}
        }
      }

      // 6. Last resort: look for any product image that might match this option
      const galleryImage = this.findImageFromGallery(optionValue);
      if (galleryImage) {
        return { featured_media: { src: galleryImage } };
      }

      return null;
    }

    findImageFromGallery(optionValue) {
      // Try to find an image that matches this option value
      const normalizedValue = optionValue.toLowerCase().trim();

      // 1. First check our product data for images with matching alt text
      const productDataEl = document.getElementById('macmerise-product-data');
      if (productDataEl) {
        try {
          const productData = JSON.parse(productDataEl.textContent);
          if (productData && productData.images) {
            // Look for image with alt text containing the option value
            for (const image of productData.images) {
              const alt = (image.alt || '').toLowerCase();
              if (alt.includes(normalizedValue) || alt === normalizedValue) {
                return image.src;
              }
            }

            // Also check if any image src contains the option value
            for (const image of productData.images) {
              const src = image.src.toLowerCase();
              const searchTerm = normalizedValue.replace(/\s+/g, '-');
              if (src.includes(searchTerm)) {
                return image.src;
              }
            }

            // Last resort: if no match found and we have images, use the first one
            // (This ensures polaroid always shows something)
            if (productData.images.length > 0 && productData.featured_image) {
              return productData.featured_image.src;
            }
          }
        } catch (e) {}
      }

      // 2. Look for product gallery images in the DOM
      const galleryImages = document.querySelectorAll('.product-gallery img, [data-product-media] img, .product__media img');
      for (const img of galleryImages) {
        const alt = (img.alt || '').toLowerCase();
        const src = img.src || img.dataset.src || '';

        // Check if alt text contains the option value
        if (alt.includes(normalizedValue)) {
          return src;
        }

        // Check if src contains the option value (some stores name images after variants)
        if (src.toLowerCase().includes(normalizedValue.replace(/\s+/g, '-'))) {
          return src;
        }
      }

      return null;
    }

    buildImageUrl(originalUrl, width = 400) {
      if (!originalUrl) return null;

      // Handle Shopify CDN URLs
      if (originalUrl.includes('cdn.shopify.com')) {
        const url = originalUrl.split('?')[0];
        const parts = url.split('.');
        const extension = parts.pop();
        const basePath = parts.join('.').replace(/_\d+x\d*/, '');
        return `${basePath}_${width}x.${extension}`;
      }

      return originalUrl;
    }

    formatPrice(cents) {
      const price = (cents / 100).toFixed(2);
      return `₹${price.replace(/\.00$/, '')}`;
    }

    truncateText(text, maxLength) {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    }

    isLightColor(hex) {
      if (!hex || !hex.startsWith('#')) return false;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      // Lower threshold to catch light greens, pastels, etc.
      return brightness > 170;
    }

    isDarkColor(colorName) {
      // List of dark color names that need white strikethrough
      const darkColors = [
        'black',
        'navy',
        'navy blue',
        'steel grey',
        'steel gray',
        'charcoal',
        'midnight',
        'midnight blue',
        'dark grey',
        'dark gray',
        'dark blue',
        'dark green',
        'bottle green',
        'burgundy',
        'maroon',
        'brown',
        'dark brown',
        'espresso',
        'coffee',
        'onyx',
        'jet black',
        'coal',
        'slate',
        'graphite',
        'gunmetal',
        'obsidian',
        'raven',
        'ink',
        'ebony'
      ];

      const normalizedColor = colorName.toLowerCase().trim();
      return darkColors.some(dark => normalizedColor.includes(dark) || dark.includes(normalizedColor));
    }

    isColorAvailable(colorName) {
      // Check if ANY variant with this color is available
      const productDataEl = document.getElementById('macmerise-product-data');
      if (!productDataEl) return true; // Assume available if no data

      try {
        const productData = JSON.parse(productDataEl.textContent);
        if (!productData || !productData.variants || !productData.options) return true;

        // Find color option position
        const colorLabels = ['color', 'colour'];
        let colorOptionIndex = -1;
        productData.options.forEach((optionName, index) => {
          if (colorLabels.some(label => optionName.toLowerCase().includes(label))) {
            colorOptionIndex = index + 1;
          }
        });

        if (colorOptionIndex === -1) return true;

        // Check if any variant with this color is available
        const normalizedColor = colorName.toLowerCase().trim();
        return productData.variants.some(variant => {
          const variantColor = (variant[`option${colorOptionIndex}`] || '').toLowerCase().trim();
          return variantColor === normalizedColor && variant.available;
        });
      } catch (e) {
        return true; // Assume available on error
      }
    }

    // --------------------------------------------------------------------------
    // Variant Image Filtering
    // --------------------------------------------------------------------------

    setupVariantImageFiltering() {
      if (this.pageType !== 'product_page') return;

      const self = this;
      // Get color option labels (case insensitive, handles "Color", "Colour", "Color:", etc.)
      const colorLabels = ['color', 'colour'];

      // Find all color option fieldsets
      const variantPickers = document.querySelectorAll('variant-picker');
      let colorFieldset = null;

      console.log('Looking for color fieldsets in', variantPickers.length, 'variant pickers');

      variantPickers.forEach(picker => {
        const fieldsets = picker.querySelectorAll('fieldset');

        fieldsets.forEach(fieldset => {
          const legend = fieldset.querySelector('legend');
          if (!legend) return;

          const legendText = legend.textContent || '';
          const optionName = legendText.toLowerCase().replace(/[^a-z\s]/g, '').trim();

          console.log('Found fieldset with legend:', legendText, '-> normalized:', optionName);

          // Check if this is a color option
          if (colorLabels.some(label => optionName.includes(label))) {
            console.log('Found color fieldset!');
            colorFieldset = fieldset;
            this.setupColorOptionListener(fieldset);
          }
        });
      });

      // Apply initial filtering after a delay to ensure everything is loaded
      if (colorFieldset) {
        setTimeout(() => {
          const selectedInput = colorFieldset.querySelector('input[type="radio"]:checked');
          console.log('Selected input:', selectedInput);

          if (selectedInput) {
            // Debug: log all data we can get from the input
            console.log('Input value:', selectedInput.value);
            console.log('Input data-option-value:', selectedInput.dataset.optionValue);

            const selectedColor = self.getOptionValueFromInput(selectedInput);
            console.log('Initial color:', selectedColor);

            if (selectedColor) {
              self.filterGalleryImages(selectedColor);
            } else {
              console.log('No color value found!');
            }
          }
        }, 200);
      } else {
        console.log('No color fieldset found');
      }
    }

    setupColorOptionListener(fieldset) {
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      const self = this;

      inputs.forEach(input => {
        // Listen on both change and click
        input.addEventListener('change', function() {
          if (this.checked) {
            const selectedColor = self.getOptionValueFromInput(this);
            self.filterGalleryImages(selectedColor);
          }
        });

        // Also listen on the label/swatch click
        const label = input.nextElementSibling;
        if (label) {
          label.addEventListener('click', function() {
            setTimeout(() => {
              if (input.checked) {
                const selectedColor = self.getOptionValueFromInput(input);
                self.filterGalleryImages(selectedColor);
              }
            }, 50);
          });
        }
      });
    }

    filterGalleryImages(selectedColor) {
      if (!selectedColor) return;

      const normalizedColor = selectedColor.toLowerCase().trim();
      console.log('Filtering gallery for color:', normalizedColor);

      // Get product data
      const productDataEl = document.getElementById('macmerise-product-data');
      if (!productDataEl) {
        console.log('No product data element found');
        return;
      }

      let productData;
      try {
        productData = JSON.parse(productDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse product data', e);
        return;
      }

      if (!productData || !productData.media || !productData.variants) {
        console.log('Missing media or variants in product data');
        return;
      }

      console.log('Product has', productData.media.length, 'media items');

      // Find all color values from variants to determine image ranges
      const colorOptions = this.getColorOptionsFromVariants(productData);
      if (colorOptions.length === 0) {
        console.log('No color options found');
        return;
      }

      console.log('Color options:', colorOptions);

      // Find images that belong to this color
      const mediaIdsToShow = this.getMediaIdsForColor(productData, normalizedColor, colorOptions);
      console.log('Media IDs to show:', Array.from(mediaIdsToShow));

      // Apply filtering to gallery
      this.applyGalleryFilter(mediaIdsToShow);
    }

    getColorOptionsFromVariants(productData) {
      const colorLabels = ['color', 'colour'];
      const options = productData.options || [];

      // Find which option position is color
      let colorOptionIndex = -1;
      options.forEach((optionName, index) => {
        if (colorLabels.some(label => optionName.toLowerCase().includes(label))) {
          colorOptionIndex = index + 1; // Shopify uses 1-based index (option1, option2, option3)
        }
      });

      if (colorOptionIndex === -1) return [];

      // Get unique color values in order
      const colorValues = [];
      const seen = new Set();
      productData.variants.forEach(variant => {
        const colorValue = variant[`option${colorOptionIndex}`];
        if (colorValue && !seen.has(colorValue.toLowerCase())) {
          seen.add(colorValue.toLowerCase());
          colorValues.push(colorValue);
        }
      });

      return colorValues;
    }

    getMediaIdsForColor(productData, selectedColor, colorOptions) {
      const media = productData.media || [];
      const mediaIdsToShow = new Set();

      // Method 1: Match by alt text
      media.forEach(item => {
        const alt = (item.alt || '').toLowerCase();
        if (alt.includes(selectedColor) || alt === selectedColor) {
          mediaIdsToShow.add(item.id);
        }
      });

      console.log('After alt text matching:', mediaIdsToShow.size, 'items');

      // Method 2: If no alt text matches, use variant featured_media
      if (mediaIdsToShow.size === 0) {
        const variants = productData.variants || [];
        const colorLabels = ['color', 'colour'];
        const options = productData.options || [];

        let colorOptionIndex = -1;
        options.forEach((optionName, index) => {
          if (colorLabels.some(label => optionName.toLowerCase().includes(label))) {
            colorOptionIndex = index + 1;
          }
        });

        if (colorOptionIndex > 0) {
          variants.forEach(variant => {
            const variantColor = (variant[`option${colorOptionIndex}`] || '').toLowerCase();
            if (variantColor === selectedColor && variant.featured_media) {
              mediaIdsToShow.add(variant.featured_media.id);
            }
          });
        }
        console.log('After variant matching:', mediaIdsToShow.size, 'items');
      }

      // Method 3: If still no matches, divide media evenly by color count
      // This assumes media are arranged in order by color
      if (mediaIdsToShow.size === 0 && media.length > 0) {
        const colorIndex = colorOptions.findIndex(c => c.toLowerCase() === selectedColor);
        console.log('Color index for', selectedColor, ':', colorIndex);

        if (colorIndex >= 0) {
          const mediaPerColor = Math.ceil(media.length / colorOptions.length);
          const startIndex = colorIndex * mediaPerColor;
          const endIndex = Math.min(startIndex + mediaPerColor, media.length);

          console.log('Media range:', startIndex, 'to', endIndex, '(', mediaPerColor, 'per color)');

          for (let i = startIndex; i < endIndex; i++) {
            mediaIdsToShow.add(media[i].id);
          }
        }
      }

      // If still nothing, show all media
      if (mediaIdsToShow.size === 0) {
        media.forEach(item => mediaIdsToShow.add(item.id));
      }

      return mediaIdsToShow;
    }

    applyGalleryFilter(mediaIdsToShow) {
      // Find gallery elements - Impact theme structure
      const gallery = document.querySelector('product-gallery, .product-gallery');
      if (!gallery) {
        console.log('No gallery found');
        return;
      }

      // Get all media items in the gallery
      const mediaItems = gallery.querySelectorAll('.product-gallery__media[data-media-id]');
      const thumbnails = gallery.querySelectorAll('.product-gallery__thumbnail');

      console.log('Found', mediaItems.length, 'media items in gallery');
      console.log('Gallery media IDs:', Array.from(mediaItems).map(m => m.dataset.mediaId));

      let firstVisibleItem = null;
      let shownCount = 0;
      let hiddenCount = 0;

      mediaItems.forEach((item, index) => {
        const mediaId = parseInt(item.dataset.mediaId);

        if (mediaIdsToShow.has(mediaId)) {
          // Show this item
          item.classList.remove('mm-gallery-hidden');
          item.removeAttribute('hidden');
          item.style.cssText = '';
          if (!firstVisibleItem) firstVisibleItem = item;
          shownCount++;
        } else {
          // Hide this item
          item.classList.add('mm-gallery-hidden');
          item.setAttribute('hidden', '');
          hiddenCount++;
        }
      });

      console.log('Shown:', shownCount, 'Hidden:', hiddenCount);

      // Also filter thumbnails - match by data-media-id on the button or by index
      thumbnails.forEach((thumb, index) => {
        const correspondingMedia = mediaItems[index];
        if (correspondingMedia) {
          const mediaId = parseInt(correspondingMedia.dataset.mediaId);
          if (mediaIdsToShow.has(mediaId)) {
            thumb.classList.remove('mm-gallery-hidden');
            thumb.removeAttribute('hidden');
            thumb.style.cssText = '';
          } else {
            thumb.classList.add('mm-gallery-hidden');
            thumb.setAttribute('hidden', '');
          }
        }
      });

      // Navigate to first visible image
      if (firstVisibleItem) {
        const carousel = gallery.querySelector('media-carousel');
        if (carousel) {
          // Find the visible index (counting only visible items)
          const visibleIndex = Array.from(mediaItems).filter(m => !m.classList.contains('mm-gallery-hidden')).indexOf(firstVisibleItem);

          // Try Impact theme's carousel API
          if (typeof carousel.goTo === 'function') {
            carousel.goTo(0);
          } else if (typeof carousel.select === 'function') {
            carousel.select(0);
          }

          // Scroll the first visible item into view
          firstVisibleItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      }

      // Trigger resize/layout recalculation
      window.dispatchEvent(new Event('resize'));

      // Force layout update for carousel
      const mediaCarousel = gallery.querySelector('media-carousel');
      if (mediaCarousel) {
        mediaCarousel.dispatchEvent(new Event('resize'));
      }
    }
  }

  // ==========================================================================
  // Initialize
  // ==========================================================================

  window.MacmeriseSwatches = new MacmeriseSwatches();

})();
